import React, { useEffect, useMemo } from "react";

import moment from "moment";
import Modal from "react-bootstrap/Modal";
import LoadingIndicator from "../../../../../components/LoadingIndicator";
import RenderWhen from "../../../../../components/RenderWhen";
import { getDynamicAnalysisList } from "../../../../../components/file-analysis-form/utils";
import {
  ErrorResponse,
  ValidationErrorResponse
} from "../../../../../requests/@types/response";
import api from "../../../../../services/api";
import { route } from "../../../../../shim/jsbridge";
import { poll } from "../../../../../utils";
import usePrepareReanalyzeData, {
  areAdditionalAnalysisTypesAvailable
} from "../../../../header/hooks/usePrepareReanalyzeData";
import {
  IReanalyzePayload,
  IReanalyzeSample,
  IReanalyzeValues,
  ISandboxStatusResponse
} from "../../../@types";
import { useReanalyze } from "../../../hooks";
import useCheckIntegrations from "../../../hooks/useCheckIntegrations";
import useSuggestedRlcsPlatform from "../../../hooks/useSuggestedRlcsPlatform";
import {
  showQuotaExceededNotification,
  showRateLimitErrorNotification,
  showRateLimitWarningNotification
} from "../../analysistable/utils";
import useRlcsPrepareReanalyzeData from "../../rlcloudsandbox/hooks/useRlcsPrepareReanalyzeData";
import { InteractiveParams, RlcsErrors, RlcsParams } from "../reanalyze-form";
import ReanalyzeForm from "../reanalyze-form/ReanalyzeForm";
import {
  QUEUED,
  QUOTA_EXCEEDED,
  QUOTA_RATE_LIMIT_CODES,
  RL_SANDBOX_MAX_TRIES,
  RL_SANDBOX_POLL_INTERVAL,
  TOO_MANY_RATE_LIMIT_FAILS,
  TOO_MANY_RATE_LIMIT_WARNING
} from "./consts";
import useReanalyzeForm from "./useReanalyzeForm";
import { prepareDataForApi } from "./utils";

type ReanalyzeProps = {
  initial: IReanalyzeValues;
  samples: IReanalyzeSample[];
  callbackOnSuccess?: () => void;
  onClose: () => void;
  onReanalyzePopupWindowSubmit?: (payload: IReanalyzePayload) => void;
};

const convertToRLCSError = (
  error?: ErrorResponse<ValidationErrorResponse> | null
): RlcsErrors => ({
  execution_timeout: error?.data?.detail?.fields?.rl_cloud_sandbox_timeout?.[0],
  sample_name: error?.data?.detail?.fields?.rl_cloud_sandbox_sample_name?.[0],
  internet_simulation:
    error?.data?.detail?.fields?.rl_cloud_sandbox_internet_simulation?.[0]
});

const convertToInteractiveRLCSError = (
  error?: ErrorResponse<ValidationErrorResponse> | null
): RlcsErrors => ({
  execution_timeout: error?.data?.detail?.fields?.interactive_timeout?.[0],
  sample_name: error?.data?.detail?.fields?.interactive_sample_name?.[0]
});

const Reanalyze = ({
  onClose,
  initial,
  samples,
  callbackOnSuccess,
  onReanalyzePopupWindowSubmit
}: ReanalyzeProps) => {
  const hashes =
    samples?.map(({ hash_value = "" }) => hash_value).filter(Boolean) ?? [];

  const {
    data: prepareReanalyzeData,
    isLoading: isPrepareReanalyzeDataLoading
  } = usePrepareReanalyzeData({
    hashes,
    skipRlcs: true
  });

  const {
    data: rlcsPrepareReanalyzeData,
    isLoading: isRlcsPrepareReanalyzeLoading
  } = useRlcsPrepareReanalyzeData({
    hash: hashes[0],
    isEnabled:
      samples.length === 1 &&
      !!prepareReanalyzeData?.dynamic_analysis?.rl_dynamic_analysis?.enabled
  });

  const { suggestedPlatform, isLoading: isSuggestedPlatformLoading } =
    useSuggestedRlcsPlatform(hashes[0]);

  const {
    values,
    rlcs,
    interactive,
    api: formApi
  } = useReanalyzeForm(initial, suggestedPlatform);

  const {
    mutateAsync: reanalyze,
    isPending: isReanalyzePending,
    reset: resetReanalyze,
    error: reanalyzeError
  } = useReanalyze();
  const { checkIntegrations } = useCheckIntegrations();

  const handleValues = (newValues: IReanalyzeValues) => {
    formApi.handleValuesChange(newValues);
  };

  const handleInteractiveParamChange = (v: InteractiveParams) => {
    resetReanalyze();
    formApi.handleInteractiveChange(v);
  };

  const handleRlcsParamsChange = (params: RlcsParams) => {
    resetReanalyze();
    formApi.handleRlcsChange(params);
  };

  const { rl_dynamic_analysis, rl_dynamic_analysis_interactive } =
    initial ?? {};

  useEffect(() => {
    if (!prepareReanalyzeData) return;
    formApi.handleValuesChange(prev => ({
      ...prev,
      tc: prepareReanalyzeData.tc?.analyzed_with_old_tc || false,
      ti_cloud:
        (prepareReanalyzeData.ti_cloud.autoupload &&
          prepareReanalyzeData.ti_cloud.enabled) ||
        false
    }));
  }, [prepareReanalyzeData, formApi]);

  useEffect(() => {
    if (!rlcsPrepareReanalyzeData) return;
    formApi.handleValuesChange(prev => ({
      ...prev,
      rl_dynamic_analysis:
        rl_dynamic_analysis &&
        rlcsPrepareReanalyzeData?.rl_dynamic_analysis.enabled,
      rl_dynamic_analysis_interactive:
        rl_dynamic_analysis_interactive &&
        rlcsPrepareReanalyzeData?.rl_dynamic_analysis_interactive.enabled
    }));
  }, [
    rlcsPrepareReanalyzeData,
    formApi,
    rl_dynamic_analysis,
    rl_dynamic_analysis_interactive
  ]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const {
      tc,
      ti_cloud: tiCloud,
      local_only,
      ...dynamicAnalysisData
    } = values ?? {};
    const dynamicAnalysisList = getDynamicAnalysisList(values);

    const checkedIntegrations = await checkIntegrations(dynamicAnalysisList);

    const updatedDynamicTools = Object.entries(dynamicAnalysisData).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: checkedIntegrations[key] ?? value
      }),
      {} as Omit<IReanalyzeValues, "tc" | "ti_cloud">
    );

    const tools: IReanalyzeValues = {
      tc,
      ti_cloud: tiCloud,
      ...updatedDynamicTools
    };

    const apiValues = prepareDataForApi(tools, rlcs, interactive, samples);

    if (!apiValues.integrations.length) {
      onClose();
      return;
    }

    reanalyze(apiValues).then(({ data }) => {
      onClose();
      callbackOnSuccess?.();
      onReanalyzePopupWindowSubmit?.(apiValues);

      const requestedDynamicAnalysis: string[] =
        data.requested_analysis?.dynamic_analysis || [];
      const samplesForAnalysis: Array<{ hash_value: string }> =
        data.requested_analysis?.samples || [];
      const timeSentToAnalysis: string | null =
        data.time_sent_to_analysis || null;
      const requestedHashes: string[] = samplesForAnalysis.map(
        ({ hash_value: hashValue }) => hashValue
      );

      if (
        requestedDynamicAnalysis.includes("rl_dynamic_analysis") &&
        requestedHashes.length
      ) {
        const sentToAnalysis = moment(timeSentToAnalysis);

        const isNewAnalysis = (changed: string): boolean =>
          sentToAnalysis.isSameOrBefore(changed);

        const isRateLimitOrQuotaHit = (item: {
          status_code: number;
          changed: string;
        }): boolean =>
          QUOTA_RATE_LIMIT_CODES.includes(item.status_code) &&
          isNewAnalysis(item.changed);

        // stop polling condition
        // if at least one status is QUOTA_EXCEEDED or every element has "known outcome" (not queued)
        const stop = ({
          data: pollingData = []
        }: {
          data?: Array<{
            status_code: number;
            changed: string;
          }>;
        }): boolean => {
          if (!pollingData.length) return false;
          return (
            pollingData.some(isRateLimitOrQuotaHit) ||
            pollingData.every(
              el => el.status_code !== QUEUED && isNewAnalysis(el.changed)
            )
          );
        };

        // Start polling
        poll(
          () =>
            api.rlSandboxAnalysisStatus({
              hash_values: requestedHashes
            }),
          stop,
          RL_SANDBOX_POLL_INTERVAL,
          RL_SANDBOX_MAX_TRIES
        ).then((response: ISandboxStatusResponse) => {
          const { data: responseData = [] } = response;
          const hasQuotaExceeded = responseData.some(
            el => el.status_code === QUOTA_EXCEEDED
          );
          const rateLimitError = responseData.some(
            el => el.status_code === TOO_MANY_RATE_LIMIT_FAILS
          );
          const rateLimitWarning = responseData.some(
            el => el.status_code === TOO_MANY_RATE_LIMIT_WARNING
          );

          if (hasQuotaExceeded) {
            showQuotaExceededNotification();
          }
          if (rateLimitError) {
            showRateLimitErrorNotification();
          }
          if (rateLimitWarning) {
            showRateLimitWarningNotification();
          }
        });
      }
      if (values.rl_dynamic_analysis_interactive) {
        samplesForAnalysis?.forEach(({ hash_value: hashValue }) =>
          window.open(
            route("rl_interactive_analysis_for_hash", [hashValue]),
            "_blank"
          )
        );
      }
    });
  };

  const completeReanalyzeData = useMemo(
    () => ({
      ...prepareReanalyzeData,
      dynamic_analysis: {
        ...prepareReanalyzeData?.dynamic_analysis,
        rl_dynamic_analysis: {
          ...prepareReanalyzeData?.dynamic_analysis?.rl_dynamic_analysis,
          ...rlcsPrepareReanalyzeData?.rl_dynamic_analysis,
          is_loading_analysis_status:
            isRlcsPrepareReanalyzeLoading || isSuggestedPlatformLoading
        },
        rl_dynamic_analysis_interactive: {
          ...(rlcsPrepareReanalyzeData?.rl_dynamic_analysis_interactive ?? {}),
          is_loading_analysis_status: isRlcsPrepareReanalyzeLoading
        }
      }
    }),
    [
      isRlcsPrepareReanalyzeLoading,
      isSuggestedPlatformLoading,
      prepareReanalyzeData,
      rlcsPrepareReanalyzeData
    ]
  );

  const modalSize =
    !isPrepareReanalyzeDataLoading &&
    areAdditionalAnalysisTypesAvailable(prepareReanalyzeData)
      ? "lg"
      : undefined;
  return (
    <Modal show onHide={onClose} size={modalSize}>
      <Modal.Header closeButton>
        <Modal.Title>Reanalyze</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <RenderWhen
          condition={!isPrepareReanalyzeDataLoading}
          fallback={
            <LoadingIndicator data-testid="reanalyze_form_is_loading" />
          }
        >
          <ReanalyzeForm
            initial={values}
            values={values}
            displayWarningMessage={(samples?.length ?? 0) > 5}
            popupWindowData={completeReanalyzeData}
            onCancel={onClose}
            onValues={handleValues}
            onSubmit={handleSubmit}
            onRlcsParamsChange={handleRlcsParamsChange}
            rlcsParams={rlcs}
            interactiveParams={interactive}
            onInteractiveParamChange={handleInteractiveParamChange}
            showFilePasswordField={false}
            interactiveAnalysisDisabled={(samples?.length ?? 0) > 1}
            isSubmitted={isReanalyzePending}
            isLoading={
              isPrepareReanalyzeDataLoading ||
              isRlcsPrepareReanalyzeLoading ||
              isSuggestedPlatformLoading
            }
            rlcsErrors={convertToRLCSError(reanalyzeError)}
            interactiveErrors={convertToInteractiveRLCSError(reanalyzeError)}
            modalLayout={modalSize}
          />
        </RenderWhen>
      </Modal.Body>
    </Modal>
  );
};

export default Reanalyze;
